import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { useAssignmentAuthoring } from "../AssignmentAuthoringProvider";

const SectionEditorContext = createContext(null);

const EMPTY_SECTION = {
  id: "",
  title: "",
  template: "custom",
  type: "custom",
  metadata: {},
};

const serialize = (value) => {
  if (value == null) {
    return "";
  }

  try {
    return JSON.stringify(value);
  } catch (error) {
    return String(value);
  }
};

const cleanupSectionsErrors = (errors) => {
  if (!errors || typeof errors !== "object") {
    return {};
  }

  const next = { ...errors };
  if (next.sections && typeof next.sections === "object") {
    const prunedSections = Object.fromEntries(
      Object.entries(next.sections).filter(([, value]) => {
        if (value == null) {
          return false;
        }

        if (typeof value === "string") {
          return value.trim().length > 0;
        }

        if (Array.isArray(value)) {
          return value.length > 0;
        }

        if (typeof value === "object") {
          return Object.keys(value).length > 0;
        }

        return true;
      })
    );

    if (Object.keys(prunedSections).length > 0) {
      next.sections = prunedSections;
    } else {
      delete next.sections;
    }
  }

  return next;
};

export const SectionEditorProvider = ({
  sectionId,
  children,
  sectionOverride = null,
}) => {
  const {
    sections = [],
    updateSection,
    validationErrors,
    setValidationErrors,
  } = useAssignmentAuthoring();

  const section = useMemo(() => {
    if (sectionOverride) {
      return { ...EMPTY_SECTION, ...sectionOverride };
    }

    if (!sectionId) {
      return EMPTY_SECTION;
    }

    return (
      sections.find((candidate) => candidate.id === sectionId) || EMPTY_SECTION
    );
  }, [sectionId, sectionOverride, sections]);

  const updateMetadata = useCallback(
    (updater) => {
      if (!updateSection || !sectionId) {
        return;
      }

      updateSection(sectionId, (prev) => {
        const previousMetadata = prev.metadata || {};
        const nextMetadata =
          typeof updater === "function"
            ? updater(previousMetadata)
            : updater || {};

        if (serialize(previousMetadata) === serialize(nextMetadata)) {
          return prev;
        }

        return {
          ...prev,
          metadata: nextMetadata,
        };
      });
    },
    [sectionId, updateSection]
  );

  const setSectionTitle = useCallback(
    (title) => {
      if (!updateSection || !sectionId) {
        return;
      }

      updateSection(sectionId, (prev) => {
        if ((prev.title || "") === (title || "")) {
          return prev;
        }

        return {
          ...prev,
          title: title || "",
        };
      });
    },
    [sectionId, updateSection]
  );

  const setSectionTemplate = useCallback(
    (template) => {
      if (!updateSection || !sectionId) {
        return;
      }

      updateSection(sectionId, (prev) => {
        const nextTemplate = template || "custom";
        if ((prev.template || "custom") === nextTemplate) {
          return prev;
        }

        return {
          ...prev,
          template: nextTemplate,
          type: nextTemplate,
        };
      });
    },
    [sectionId, updateSection]
  );

  const setSectionErrors = useCallback(
    (errors) => {
      if (!setValidationErrors || !sectionId) {
        return;
      }

      setValidationErrors((prevErrors) => {
        const nextErrors = cleanupSectionsErrors(prevErrors || {});
        const sectionsErrors = { ...(nextErrors.sections || {}) };

        const hasErrors = !!(
          errors &&
          ((typeof errors === "string" && errors.trim().length > 0) ||
            (Array.isArray(errors) && errors.length > 0) ||
            (typeof errors === "object" && Object.keys(errors).length > 0))
        );

        if (hasErrors) {
          sectionsErrors[sectionId] = errors;
        } else {
          delete sectionsErrors[sectionId];
        }

        if (Object.keys(sectionsErrors).length > 0) {
          return {
            ...nextErrors,
            sections: sectionsErrors,
          };
        }

        const pruned = { ...nextErrors };
        delete pruned.sections;
        return pruned;
      });
    },
    [sectionId, setValidationErrors]
  );

  const sectionErrors = useMemo(() => {
    if (!validationErrors || typeof validationErrors !== "object") {
      return null;
    }

    if (
      validationErrors.sections &&
      typeof validationErrors.sections === "object"
    ) {
      return validationErrors.sections[sectionId] || null;
    }

    return null;
  }, [sectionId, validationErrors]);

  const value = useMemo(
    () => ({
      section,
      updateMetadata,
      setSectionTitle,
      setSectionTemplate,
      setSectionErrors,
      sectionErrors,
    }),
    [
      section,
      updateMetadata,
      setSectionTitle,
      setSectionTemplate,
      setSectionErrors,
      sectionErrors,
    ]
  );

  return (
    <SectionEditorContext.Provider value={value}>
      {children}
    </SectionEditorContext.Provider>
  );
};

export const useSectionEditor = () => {
  const context = useContext(SectionEditorContext);
  if (!context) {
    throw new Error(
      "useSectionEditor must be used within a SectionEditorProvider"
    );
  }
  return context;
};

export default SectionEditorContext;
